import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircle2, Copy, Package, ArrowRight, Download } from "lucide-react";
import { toast } from "sonner";
import { downloadRegistrationCard } from "@/lib/registrationCard";

export default function Confirmation() {
  const { registrationNumber } = useParams();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    api.get(`/orders/${registrationNumber}`).then((r) => setOrder(r.data));
  }, [registrationNumber]);

  const copy = () => {
    navigator.clipboard.writeText(registrationNumber);
    toast.success("Registration number copied");
  };

  if (!order) return <div className="pt-32 text-center text-white/60">Loading…</div>;

  return (
    <div className="pt-28 pb-20 px-6 lg:px-10 relative z-10">
      <div className="max-w-3xl mx-auto text-center">
        <div className="w-16 h-16 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 mx-auto flex items-center justify-center">
          <CheckCircle2 className="w-8 h-8 text-gold" />
        </div>
        <div className="overline text-gold mt-6">Registration Successful</div>
        <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight mt-2">
          Welcome to the registry, {order.customer.full_name.split(" ")[0]}.
        </h1>
        <p className="text-white/60 mt-4">
          Your commemorative plot has been reserved. Our team will prepare and ship your printed deed within 48 hours.
        </p>

        <div className="mt-10 glass rounded-3xl p-8 text-left" data-testid="confirmation-card">
          <div className="overline">Registration Number</div>
          <div className="flex items-center justify-between mt-2">
            <div className="font-mono-space text-2xl lg:text-3xl text-gold tracking-wide" data-testid="reg-number">
              {order.registration_number}
            </div>
            <button onClick={copy} className="btn-ghost !py-2 !px-3 text-xs" data-testid="copy-reg">
              <Copy className="w-3.5 h-3.5" /> Copy
            </button>
          </div>

          <div className="mt-8 grid sm:grid-cols-2 gap-6 border-t border-white/10 pt-6">
            <Item k="Certificate Name" v={order.certificate_name || order.customer.full_name} />
            <Item k="Planet" v={order.planet === "moon" ? "The Moon" : "Mars"} />
            <Item k="Location" v={order.marker_label} />
            <Item k="Coordinates" v={`LAT ${order.lat.toFixed(2)}° · LON ${order.lon.toFixed(2)}°`} mono />
            <Item k="Plot Size" v={`${order.acres} ${order.acres === 1 ? "Acre" : "Acres"}`} />
            <Item k="Amount Paid" v={`₹${order.amount_inr.toLocaleString()}`} mono />
            <Item k="Order Status" v={<span className="text-gold">{order.courier_status.toUpperCase()}</span>} />
            <Item k="Payment" v={<span className="text-emerald-400">{order.payment_status.toUpperCase()}</span>} />
          </div>

          <div className="mt-8 p-4 border border-white/10 rounded-xl flex gap-3 items-start">
            <Package className="w-5 h-5 text-gold mt-0.5 shrink-0" />
            <div className="text-sm text-white/70">
              Your premium printed deed is prepared exclusively by Teuralink admins. It cannot be downloaded, printed by you, or generated digitally. This preserves its status as a physical keepsake.
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          <button
            onClick={() => {
              downloadRegistrationCard(order);
              toast.success("Registration number card downloaded");
            }}
            className="btn-primary"
            data-testid="download-reg-card"
          >
            <Download className="w-4 h-4" /> Download Registration Number
          </button>
          <Link to="/dashboard" className="btn-ghost" data-testid="go-dashboard">Go to My Dashboard <ArrowRight className="w-4 h-4" /></Link>
          <Link to="/verify" className="btn-ghost">Verify Registration</Link>
        </div>
        <div className="mt-3 text-[11px] text-white/40">
          The downloadable file contains only your registration number and metadata — it is <span className="text-white/60">not</span> the physical deed.
        </div>
      </div>
    </div>
  );
}

function Item({ k, v, mono }) {
  return (
    <div>
      <div className="overline">{k}</div>
      <div className={`mt-1 ${mono ? "font-mono-space text-sm" : "text-lg"} text-white`}>{v}</div>
    </div>
  );
}
